import { memo, useCallback } from 'react';

import { WorkspaceAgentAssetMentionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentAssetMentionRow';
import { WorkspaceAgentBoardTreeRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentBoardTreeRow';
import { WorkspaceAgentBrandKitMentionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentBrandKitMentionRow';
import { getWorkspaceAgentSelectAssetOptionId } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/getWorkspaceAgentSelectAssetOptionId';
import { type WorkspaceAgentMentionRowItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { isWorkspaceAgentMentionTreeItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';

export type WorkspaceAgentSelectAssetListRowProps = {
  getIsBoardItemDisabled?: (boardId: string) => boolean;
  isKeyboardHighlighted: boolean;
  isPendingChildCount: boolean;
  isSelected: boolean;
  item: WorkspaceAgentMentionRowItem;
  itemIndex: number;
  onHighlight: (index: number) => void;
  onSelectItem: (itemId: string) => void;
  onToggleExpanded: ((nodeId: string, isExpanded: boolean) => void) | undefined;
};

export const WorkspaceAgentSelectAssetListRow = memo(
  ({
    getIsBoardItemDisabled,
    isKeyboardHighlighted,
    isPendingChildCount,
    isSelected,
    item,
    itemIndex,
    onHighlight,
    onSelectItem,
    onToggleExpanded,
  }: WorkspaceAgentSelectAssetListRowProps) => {
    const optionId = getWorkspaceAgentSelectAssetOptionId(item.id);

    const handleHighlight = useCallback(() => {
      onHighlight(itemIndex);
    }, [itemIndex, onHighlight]);

    const handleSelect = useCallback(() => {
      onSelectItem(item.id);
    }, [item.id, onSelectItem]);

    if (isWorkspaceAgentMentionTreeItem(item)) {
      const boardId = item.kind === 'board' ? item.boardId : undefined;
      const isDisabled = boardId ? getIsBoardItemDisabled?.(boardId) : false;

      return (
        <WorkspaceAgentBoardTreeRow
          id={optionId}
          index={itemIndex}
          isDisabled={isDisabled}
          isKeyboardHighlighted={isKeyboardHighlighted}
          isPendingChildCount={isPendingChildCount}
          isSelected={isSelected}
          item={item}
          onHighlight={handleHighlight}
          onSelect={handleSelect}
          onToggleExpanded={onToggleExpanded}
        />
      );
    }

    if (item.kind === 'contextSourceFieldValue') {
      return (
        <WorkspaceAgentBrandKitMentionRow
          id={optionId}
          index={itemIndex}
          isKeyboardHighlighted={isKeyboardHighlighted}
          isSelected={isSelected}
          item={item}
          onHighlight={handleHighlight}
          onSelect={handleSelect}
        />
      );
    }

    return (
      <WorkspaceAgentAssetMentionRow
        id={optionId}
        index={itemIndex}
        isKeyboardHighlighted={isKeyboardHighlighted}
        isSelected={isSelected}
        item={item}
        onHighlight={handleHighlight}
        onSelect={handleSelect}
      />
    );
  },
);

WorkspaceAgentSelectAssetListRow.displayName = 'WorkspaceAgentSelectAssetListRow';
